import { useTranslation } from "react-i18next";
import type { CollectionVersionSummary } from "../../../../api/content/contentDecoders";
import Field from "../../../../ui/forms/Field";
import { FORM_SELECT_CLASS } from "../../../../ui/styles/formStyles";
import WorkflowContentVersions from "../../../versions/contentVersions/WorkflowContentVersions";
import { labelOf, useVersionLabels } from "../../../versions/labels/useVersionLabels";
import { versionChoiceText } from "../../../versions/labels/versionChoiceText";
import { processTerm } from "../../../../api/content/processTerms";
import { classText } from "../../../workflows/inputCards/typeWords";

interface RunVersionsProps {
  projectId: string;
  /** The project content's versions, newest first. */
  versions: readonly CollectionVersionSummary[];
  /** The chosen content version, `<collection id>@<commit>`. */
  chosen: string | null;
  /** The Workflow chosen, `<item id>@<version>`; `null` for a free Thread. */
  workflow: string | null;
  onChoose: (reference: string) => void;
}

/**
 * Which content version the run binds (T-3, F28): a detail behind "Versions", the newest
 * unless chosen. With a Workflow, only the versions that changed something it reads, by
 * label (ADR-0033 D5); a free Thread chooses among all of them. `features/versions/` names
 * and lists the versions.
 */
export default function RunVersions({ projectId, versions, chosen, workflow, onChoose }: RunVersionsProps) {
  const { t, i18n } = useTranslation();
  const labels = useVersionLabels(projectId, { itemVersions: [], collectionVersions: workflow === null ? versions.map((version) => version.reference) : [] });
  if (versions.length < 2) {
    return null;
  }
  return (
    <details className="text-sm">
      <summary className="app-text-muted cursor-pointer">{t("threads.prepare.run.versions")}</summary>
      <div className="mt-2">
        {workflow !== null ? (
          <WorkflowContentVersions projectId={projectId} workflow={workflow} chosen={chosen} onChoose={(version) => { onChoose(version.reference); }} />
        ) : (
          <Field label={classText(processTerm("ContentVersion"), t)} hint={t("threads.prepare.run.contentVersionHint")}>
            {(control) => (
              <select {...control} className={FORM_SELECT_CLASS} value={chosen ?? ""} onChange={(event) => { onChoose(event.target.value); }}>
                {versions.map((version, index) => (
                  <option key={version.reference} value={version.reference}>
                    {versionChoiceText(t, null, labelOf(labels, version.reference), version, index === 0 ? [t("threads.prepare.run.newest")] : [], i18n.resolvedLanguage)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
      </div>
    </details>
  );
}
